/**
 * Phase 101du.3 (Feb 2026) — Gestion admin des participations Corporate.
 *
 * Monté dans DistributorRow.jsx (section Corporate) — permet à l'admin de :
 *   - Calculer un rapport de participation pour un mois donné
 *   - Le finaliser (génère la facture QR-Bill)
 *   - L'envoyer par email au corporate_billing_email
 *   - Marquer comme payé
 *   - Supprimer un DRAFT
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Calculator, FileText, Send, CheckCircle2, Trash2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useConfirm } from "../ConfirmDialog";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;

const STATUS_META = {
  DRAFT: { bg: "#FBF4E9", fg: "#8C6B2F", label: "Brouillon" },
  FINALIZED: { bg: "#EAE0F2", fg: "#4A356B", label: "Finalisé" },
  SENT: { bg: "#FBEEEA", fg: "#A04E2C", label: "Envoyé" },
  PAID: { bg: "#E8F2EC", fg: "#1F4E3D", label: "Payé" },
  CANCELLED: { bg: "#F5F5F0", fg: "#8A8D8A", label: "Annulé" },
};

export default function CorporateParticipationSection({ distId }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // JS 0-based → 1-based
  const [busyId, setBusyId] = useState(null);
  const confirm = useConfirm();

  const yearOptions = useMemo(() => {
    const cur = now.getFullYear();
    return [cur - 1, cur, cur + 1];
  }, []); // now is a component-scope constant, no reactive deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/corporate/${distId}/participation-reports`);
      setReports(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [distId]);

  useEffect(() => { load(); }, [load]);

  const doCompute = async () => {
    setBusyId("compute");
    try {
      const { data } = await api.post(
        `/corporate/${distId}/participation-reports/compute?year=${year}&month=${month}`,
        {},
      );
      toast.success(`Rapport ${data.period_label} calculé — participation TTC ${fmtCHF(data.participation_ttc_chf)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur calcul");
    } finally {
      setBusyId(null);
    }
  };

  const doFinalize = async (report) => {
    const ok = await confirm({
      title: `Finaliser ${report.period_label} ?`,
      description: `Cette action va générer une facture Swiss QR-Bill de ${fmtCHF(report.participation_ttc_chf)} et lier le rapport à cette facture. Le rapport ne pourra plus être modifié.`,
      confirmText: "Finaliser & créer la facture",
    });
    if (!ok) return;
    setBusyId(report.id);
    try {
      const { data } = await api.post(`/corporate/participation-reports/${report.id}/finalize`, {});
      toast.success(`Facture générée · ${data.invoice_id ? data.invoice_id.slice(0, 8) : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur finalisation");
    } finally {
      setBusyId(null);
    }
  };

  const doSend = async (report) => {
    const ok = await confirm({
      title: `Envoyer la facture ${report.period_label} par email ?`,
      description: "Un email Resend sera envoyé au corporate_billing_email du distributeur avec un lien vers le portail Corporate pour consulter la facture QR-Bill.",
      confirmText: "Envoyer maintenant",
    });
    if (!ok) return;
    setBusyId(report.id);
    try {
      await api.post(`/corporate/participation-reports/${report.id}/send-email`, {});
      toast.success("Email envoyé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi email");
    } finally {
      setBusyId(null);
    }
  };

  const doMarkPaid = async (report) => {
    const today = new Date().toISOString().slice(0, 10);
    const paymentDate = window.prompt(
      `Date de paiement (YYYY-MM-DD) pour ${report.period_label} ?`,
      today,
    );
    if (!paymentDate) return;
    const ref = window.prompt("Référence de paiement (optionnel) :", "") || "";
    setBusyId(report.id);
    try {
      await api.post(`/corporate/participation-reports/${report.id}/mark-paid`, {
        payment_date: paymentDate,
        payment_reference: ref,
      });
      toast.success("Rapport marqué comme payé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur mark-paid");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (report) => {
    const ok = await confirm({
      title: `Supprimer le brouillon ${report.period_label} ?`,
      description: "Cette action est réversible via un nouveau calcul.",
      confirmText: "Supprimer",
    });
    if (!ok) return;
    setBusyId(report.id);
    try {
      await api.delete(`/corporate/participation-reports/${report.id}`);
      toast.success("Brouillon supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#DBC9EE]" data-testid={`corporate-participation-section-${distId}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[13px] font-semibold text-[#4A356B] flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5" />
          Rapports de participation
        </div>
        <button
          type="button"
          onClick={load}
          className="text-[11px] text-[#8C6B2F] hover:text-[#6F531F] flex items-center gap-1"
          data-testid={`corp-participation-refresh-${distId}`}
        >
          <RefreshCw className="w-3 h-3" /> Actualiser
        </button>
      </div>

      {/* Compute nouveau rapport */}
      <div className="bg-[#F7F3ED] border border-[#E5DBC1] rounded-lg p-3 flex items-end gap-2 flex-wrap mb-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#8C6B2F]">Mois</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid={`corp-participation-month-${distId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_FR.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#8C6B2F]">Année</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-24 text-xs" data-testid={`corp-participation-year-${distId}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          onClick={doCompute}
          disabled={busyId === "compute"}
          className="bg-[#4A356B] hover:bg-[#3B2A56] text-white h-8"
          data-testid={`corp-participation-compute-${distId}`}
        >
          {busyId === "compute" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
          Calculer / Actualiser
        </Button>
      </div>

      {/* Table des rapports */}
      {loading ? (
        <div className="flex items-center gap-2 py-2 text-xs text-[#575A57]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…
        </div>
      ) : reports.length === 0 ? (
        <div className="text-[11px] italic text-[#A3A6A3] py-2">Aucun rapport calculé — utilisez « Calculer / Actualiser » ci-dessus.</div>
      ) : (
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-[11px] border-collapse" data-testid={`corp-participation-table-${distId}`}>
            <thead className="sticky top-0 bg-[#F5F5F0] z-[1]">
              <tr className="text-[10px] uppercase tracking-wider text-[#575A57] border-b border-[#E5E5E0]">
                <th className="text-left py-1.5 px-2 font-semibold">Période</th>
                <th className="text-right py-1.5 px-2 font-semibold">CA</th>
                <th className="text-right py-1.5 px-2 font-semibold">Report reçu</th>
                <th className="text-right py-1.5 px-2 font-semibold">CA retenu</th>
                <th className="text-right py-1.5 px-2 font-semibold">Report suivant</th>
                <th className="text-right py-1.5 px-2 font-semibold">Part. TTC</th>
                <th className="text-left py-1.5 px-2 font-semibold">Statut</th>
                <th className="text-left py-1.5 px-2 font-semibold">Facture</th>
                <th className="text-right py-1.5 px-2 font-semibold w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const s = STATUS_META[r.status] || STATUS_META.DRAFT;
                const isBusy = busyId === r.id;
                return (
                  <tr key={r.id} className="border-b border-[#F0F0EB]" data-testid={`corp-participation-row-${r.id}`}>
                    <td className="py-1.5 px-2 font-medium">{r.period_label}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmtCHF(r.revenue_chf)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-[#575A57]" data-testid={`corp-carry-in-${r.id}`}>{r.carry_in_chf ? fmtCHF(r.carry_in_chf) : "—"}</td>
                    <td className="py-1.5 px-2 text-right font-mono font-semibold" data-testid={`corp-effective-${r.id}`}>{fmtCHF(r.effective_revenue_chf ?? r.revenue_chf)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-[#1F4E3D]" data-testid={`corp-carry-out-${r.id}`}>{r.carry_out_chf ? fmtCHF(r.carry_out_chf) : "—"}</td>
                    <td className="py-1.5 px-2 text-right font-mono font-semibold text-[#A04E2C]">
                      {fmtCHF(r.participation_ttc_chf)}
                    </td>
                    <td className="py-1.5 px-2">
                      <span
                        className="inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full"
                        style={{ background: s.bg, color: s.fg }}
                      >
                        {s.label}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 font-mono text-[10px] text-[#575A57]">
                      {r.invoice_id ? <span title={r.invoice_id}>{r.invoice_id.slice(0, 8)}</span> : "—"}
                    </td>
                    <td className="py-1.5 px-2 text-right whitespace-nowrap">
                      {r.status === "DRAFT" && Number(r.participation_ttc_chf) > 0 && (
                        <button
                          disabled={isBusy}
                          onClick={() => doFinalize(r)}
                          title="Finaliser & créer la facture"
                          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#EAE0F2] text-[#4A356B]"
                          data-testid={`corp-participation-finalize-${r.id}`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {(r.status === "FINALIZED" || r.status === "SENT") && (
                        <button
                          disabled={isBusy}
                          onClick={() => doSend(r)}
                          title={r.status === "SENT" ? "Renvoyer l'email" : "Envoyer par email"}
                          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#FBEEEA] text-[#A04E2C]"
                          data-testid={`corp-participation-send-${r.id}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {(r.status === "FINALIZED" || r.status === "SENT") && (
                        <button
                          disabled={isBusy}
                          onClick={() => doMarkPaid(r)}
                          title="Marquer comme payé"
                          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#E8F2EC] text-[#1F4E3D]"
                          data-testid={`corp-participation-mark-paid-${r.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {r.status === "DRAFT" && (
                        <button
                          disabled={isBusy}
                          onClick={() => doDelete(r)}
                          title="Supprimer le brouillon"
                          className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-[#FCEEEA] text-[#C05C47]"
                          data-testid={`corp-participation-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {isBusy && <Loader2 className="w-3.5 h-3.5 animate-spin inline-block text-[#8C6B2F]" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
